/**
 * @taskId T649
 * @see functional/Cycles/cycle-5/tasks.md HU-25-FE
 */
import {
  DollarSign,
  ShieldAlert,
  Store,
  TrendingUp,
} from 'lucide-react';
import { KpiCard, Skeleton, type KpiCardProps } from '@flexibilitypay/ui';

import type { DashboardKpiResponse } from './api';

// ─── Formatters ───────────────────────────────────────────────────────────────

function formatArs(value: number): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)} %`;
}

function formatVariation(
  pct: number | null,
): KpiCardProps['trend'] | undefined {
  if (pct === null) return undefined;
  const direction = pct > 0 ? 'up' : pct < 0 ? 'down' : 'neutral';
  const abs = Math.abs(pct).toFixed(1);
  return { direction, label: `${pct > 0 ? '+' : ''}${abs} %` };
}

// ─── Component ────────────────────────────────────────────────────────────────

interface KpiCardsProps {
  data: DashboardKpiResponse | undefined;
  isLoading: boolean;
}

export function KpiCards({ data, isLoading }: KpiCardsProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-[100px] rounded-xl" />
        ))}
      </div>
    );
  }

  const cards: KpiCardProps[] = [
    {
      label: 'AUM',
      value: data ? formatArs(data.aum) : '—',
      hint: 'Saldo total bajo administración',
      icon: DollarSign,
      accent: 'violet',
      trend: formatVariation(data?.aumVariationPercent ?? null),
    },
    {
      label: 'Merchants activas',
      value: data ? String(data.merchantsActive) : '—',
      hint: 'Merchants con actividad en el período',
      icon: Store,
      accent: 'emerald',
      trend: formatVariation(data?.merchantsActiveVariationPercent ?? null),
    },
    {
      label: 'Tasa de éxito',
      value: data ? formatPercent(data.successRate) : '—',
      hint: 'Transacciones aprobadas / total',
      icon: TrendingUp,
      accent: 'sky',
      trend: formatVariation(data?.successRateVariationPercent ?? null),
    },
    {
      label: 'Alertas AML',
      value: data ? String(data.alertsAml) : '—',
      hint: 'Alertas pendientes de revisión',
      icon: ShieldAlert,
      accent: 'rose',
      trend:
        data?.alertsAmlVariationPercent !== null
          ? {
              direction:
                (data?.alertsAmlVariationPercent ?? 0) > 0 ? 'down' : 'up',
              label: `${(data?.alertsAmlVariationPercent ?? 0) > 0 ? '+' : ''}${Math.abs(data?.alertsAmlVariationPercent ?? 0).toFixed(1)} %`,
            }
          : undefined,
    },
  ];

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((kpi) => (
        <KpiCard key={kpi.label} {...kpi} />
      ))}
    </div>
  );
}
